import React, { useState, useEffect } from 'react';

const FIELD_TYPES = {
  text: { label: 'Text', inputType: 'text' },
  email: { label: 'Email', inputType: 'email' },
  phone: { label: 'Phone', inputType: 'tel' },
  date: { label: 'Date', inputType: 'date' },
  dropdown: { label: 'Dropdown' },
  radio: { label: 'Radio' },
  checkbox: { label: 'Checkbox' },
};

const inputBase = (errors, key) =>
  `w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-[#1e1e22] text-[#151521] dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
    errors[key] ? 'border-red-500 focus:ring-red-400' : 'border-[#e4e4eb] dark:border-white/10 focus:ring-[--primary-color]'
  }`;

const WidgetFormModal = ({ fields, onSubmit, onClose, primary, theme }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const getFieldKey = (field) => field.label;

  const handleChange = (field, value) => {
    const key = getFieldKey(field);
    setValues(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    fields.forEach((field) => {
      const key = getFieldKey(field);
      const val = values[key];
      if (field.required && (!val || (typeof val === 'string' && !val.trim()))) {
        newErrors[key] = `${field.label} is required`;
      } else if (val && field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[key] = 'Invalid email address';
      } else if (val && field.type === 'phone' && !/^[\d\s\-\+\(\)]{7,20}$/.test(val)) {
        newErrors[key] = 'Invalid phone number';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const ordered = {};
    fields.sort((a, b) => a.order - b.order).forEach(f => { ordered[getFieldKey(f)] = values[getFieldKey(f)]; });
    onSubmit(ordered);
  };

  const renderField = (field) => {
    const key = getFieldKey(field);
    const base = inputBase(errors, key);
    const fieldId = key.replace(/\s+/g, '_').toLowerCase();
    const commonProps = {
      id: fieldId,
      placeholder: field.placeholder,
      required: field.required,
      value: values[key] || '',
      onChange: (e) => handleChange(field, e.target.value),
      className: base,
    };

    switch (field.type) {
      case 'dropdown':
        return (
          <select {...commonProps}>
            <option value="">Select...</option>
            {(field.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="flex flex-col gap-2">
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-2.5 cursor-pointer text-sm text-[#151521] dark:text-white">
                <input
                  type="radio"
                  name={key}
                  value={opt}
                  checked={values[key] === opt}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="w-4 h-4 accent-[--primary-color]"
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div className="flex flex-col gap-2">
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-2.5 cursor-pointer text-sm text-[#151521] dark:text-white">
                <input
                  type="checkbox"
                  value={opt}
                  checked={(values[key] || []).includes(opt)}
                  onChange={(e) => {
                    const current = values[key] || [];
                    const next = e.target.checked
                      ? [...current, opt]
                      : current.filter(v => v !== opt);
                    handleChange(field, next);
                  }}
                  className="w-4 h-4 accent-[--primary-color]"
                />
                {opt}
              </label>
            ))}
          </div>
        );
      default:
        return <input type={FIELD_TYPES[field.type]?.inputType || 'text'} {...commonProps} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => {}}
      />
      {/* Modal */}
      <div
        className={`relative bg-white dark:bg-[#141418] rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto transition-all duration-300 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#141418] z-10 flex items-center justify-between px-6 pt-6 pb-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: primary ? `${primary}20` : '#3B82F620' }}
            >
              <svg className="w-5 h-5" style={{ color: primary || '#3B82F6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-[#151521] dark:text-white">Please fill in your details</h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-4 pb-6">
          <form onSubmit={handleSubmit}>
            {fields.sort((a, b) => a.order - b.order).map((field) => {
              const key = getFieldKey(field);
              return (
              <div key={key} className="mb-4">
                <label htmlFor={key.replace(/\s+/g, '_').toLowerCase()} className="block text-sm font-medium text-[#151521] dark:text-white mb-1.5">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderField(field)}
                {errors[key] && <p className="text-red-500 text-xs mt-1.5">{errors[key]}</p>}
              </div>
              );
            })}

            <div className="flex flex-col-reverse sm:flex-row gap-2 mt-6">
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 px-4 text-sm font-medium text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 active:brightness-90"
                style={{ backgroundColor: primary || '#3B82F6' }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </span>
                ) : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WidgetFormModal;
